import {
	Button,
	DrawerBody,
	DrawerFooter,
	DrawerHeader,
	DrawerHeading,
	Id,
	Separator,
	isSuccess,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ArchivedContactBanner } from "../../../../contacts/view/ArchivedContactBanner";
import { ContactUpdateCompanyDiffRow } from "../../../../contactUpdateInquiries/details/contactLists/contactUpdate/ContactUpdateCompanyDiffRow";
import { contactService } from "../../../../shared/api/contactService";
import { contactUpdateInquiryService } from "../../../../shared/api/contactUpdateInquiryService";
import { ContactUpdateInquiryContactDiffResource } from "../../../../shared/types/contactUpdateInquiry";
import { ContactUpdateAddressDiffRow } from "./ContactUpdateAddressDiffRow";
import { ContactUpdateAttributeDiffRow } from "./ContactUpdateAttributeDiffRow";
import { ContactUpdateColumnHeader } from "./ContactUpdateColumnHeader";
import { ContactUpdateEmailDiffRow } from "./ContactUpdateEmailDiffRow";
import { ContactUpdateNameCard } from "./ContactUpdateNameCard";
import { ContactUpdatePhoneDiffRow } from "./ContactUpdatePhoneDiffRow";
import { ContactUpdateFormValues } from "./contactUpdateTypes";
import {
	addressDiffEntryToIdentifier,
	attributeDiffEntryToIdentifier,
	companyDiffEntryToIdentifier,
	emailDiffEntryToIdentifier,
	mapContactUpdateFormValuesToRequest,
	mapContactUpdateInquiryContactDiffResourceToFormValues,
	phoneDiffEntryToIdentifier,
} from "./contactUpdateUtils";

interface Props {
	onClose: () => void;
	contactUpdateDiff: ContactUpdateInquiryContactDiffResource;
	inquiryId: Id;
}

const Grid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: ${({ theme }) => theme.spacing(4)};
	overflow: auto;
	padding: 0 ${({ theme }) => theme.spacing(4)} ${({ theme }) => theme.spacing(4)} ${({ theme }) => theme.spacing(4)};
`;

const StyledBody = styled(DrawerBody)`
	display: grid;
	grid-auto-flow: row;
	grid-auto-rows: max-content max-content max-content 1fr;
	overflow: hidden;
	padding: 0;
`;

const StyledSeparator = styled(Separator)`
	padding-top: ${({ theme }) => theme.spacing(4)};
`;

const StyledForm = styled(Form)`
	display: flex;
	flex-direction: column;
	overflow: hidden;
	height: 100%;
`;

const HeaderGrid = styled.div<{ $hasScrolled: boolean; $scrollbarWidth: number }>`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	padding: ${({ theme }) => theme.spacing(4)}
		calc(${({ theme }) => theme.spacing(4)} + ${({ $scrollbarWidth }) => `${$scrollbarWidth}px`})
		${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
	box-shadow: ${({ theme, $hasScrolled }) => ($hasScrolled ? theme.shadows.l1 : "none")};
`;

function isNotSame(entry: { isSame: boolean }): boolean {
	return !entry.isSame;
}

export const ContactUpdateDiffDrawerContent: FC<Props> = ({ inquiryId, contactUpdateDiff, onClose }) => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();
	const [hasScrolled, setHasScrolled] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	const [scrollbarWidth, setScrollbarWidth] = useState(0);

	useEffect(() => {
		if (ref.current !== null) {
			setScrollbarWidth(ref.current.offsetWidth - ref.current.clientWidth);
			const container = ref.current;

			const listener = () => setHasScrolled(container.scrollTop > 0);
			container.addEventListener("scroll", listener);
			return () => {
				container.removeEventListener("scroll", listener);
			};
		}
	}, []);

	const formValues: ContactUpdateFormValues = mapContactUpdateInquiryContactDiffResourceToFormValues(contactUpdateDiff);

	const handleSubmit = async (formValues: ContactUpdateFormValues) => {
		const request = mapContactUpdateFormValuesToRequest(formValues, contactUpdateDiff);

		const response = await contactUpdateInquiryService.contactUpdate(inquiryId, contactUpdateDiff.contactId, request);

		if (isSuccess(response)) {
			showToast({
				title: t("Update inquiry"),
				type: "success",
				body: t("Update inquiry saved successfully."),
			});
			onClose();
			void queryClient.invalidateQueries({ queryKey: contactUpdateInquiryService.listKey, exact: true });
			void queryClient.invalidateQueries({ queryKey: contactUpdateInquiryService.detailsKey(inquiryId), exact: true });
			void queryClient.invalidateQueries({
				predicate: ({ queryKey }) => queryKey.includes(contactUpdateInquiryService.contactsWithInquiryKeySuffix),
			});

			void queryClient.invalidateQueries({ queryKey: contactService.showKey(contactUpdateDiff.contactId) });
		}

		return false;
	};

	return (
		<Formik initialValues={formValues} onSubmit={handleSubmit}>
			<StyledForm>
				<DrawerHeader onClose={onClose}>
					<DrawerHeading>{t("Update inquiry")}</DrawerHeading>
				</DrawerHeader>
				<StyledBody>
					<ContactUpdateNameCard contact={contactUpdateDiff} />
					{contactUpdateDiff.isArchived && (
						<ArchivedContactBanner id={contactUpdateDiff.contactId} shouldNavigate={false} />
					)}
					<StyledSeparator />
					<HeaderGrid $hasScrolled={hasScrolled} $scrollbarWidth={scrollbarWidth}>
						<ContactUpdateColumnHeader title={t("Current data")} selectedSide="currentContact" />
						<ContactUpdateColumnHeader title={t("New data")} selectedSide="updatedContact" />
					</HeaderGrid>
					<Grid ref={ref}>
						{contactUpdateDiff.emails.filter(isNotSame).map((emailRow) => (
							<ContactUpdateEmailDiffRow key={emailDiffEntryToIdentifier(emailRow.currentContact)} row={emailRow} />
						))}
						{contactUpdateDiff.phones.filter(isNotSame).map((phoneRow) => (
							<ContactUpdatePhoneDiffRow key={phoneDiffEntryToIdentifier(phoneRow.currentContact)} row={phoneRow} />
						))}
						{contactUpdateDiff.addresses.filter(isNotSame).map((addressRow) => (
							<ContactUpdateAddressDiffRow
								key={addressDiffEntryToIdentifier(addressRow.currentContact)}
								row={addressRow}
							/>
						))}
						{contactUpdateDiff.companies.filter(isNotSame).map((companyRow) => (
							<ContactUpdateCompanyDiffRow
								key={companyDiffEntryToIdentifier(companyRow.currentContact)}
								row={companyRow}
							/>
						))}
						{contactUpdateDiff.attributes.filter(isNotSame).map((attributeRow) => (
							<ContactUpdateAttributeDiffRow
								key={attributeDiffEntryToIdentifier(attributeRow.currentContact)}
								row={attributeRow}
							/>
						))}
					</Grid>
				</StyledBody>
				<DrawerFooter>
					<Button disabled={contactUpdateDiff.isArchived}>{t("Submit")}</Button>
				</DrawerFooter>
			</StyledForm>
		</Formik>
	);
};
